import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dropdown } from '../shared/Dropdown';
import { Mill, StateType } from '../../lib/types';
import { ScenarioSelector } from './ScenarioSelector';
import { ChromeBar } from '../shared/ChromeBar';
import { BrandLogo } from '../shared/BrandLogo';
import { SfiLogo } from '../shared/SfiLogo';
import { DemoBadge } from '../shared/DemoBadge';
import { MobileBottomSheet } from '../shared/MobileBottomSheet';
import { AboutModal } from '../shared/AboutModal';
import { FeedbackPanelLink } from '../shared/FeedbackPanelLink';
import { useIsMobile } from '../../hooks/useIsMobile';
import { isPerCrownAvailable } from '../../hooks/usePerCrownNativeWindow';

interface LeftPanelProps {
  selectedState: StateType | null;
  selectedMill: Mill | null;
  millsInState: Mill[];
  onStateChange: (state: StateType | null) => void;
  onMillChange: (mill: Mill | null) => void;
  // Visual nudge flag from ?mode=scenario. Passed through to ScenarioSelector
  // for a brief pulse on mount — no modal, just a "look here".
  highlightScenarios?: boolean;
}

const STATES: StateType[] = ['California', 'Oregon', 'Georgia', 'Alabama'];

export const LeftPanel: React.FC<LeftPanelProps> = ({
  selectedState,
  selectedMill,
  millsInState,
  onStateChange,
  onMillChange,
  highlightScenarios = false,
}) => {
  const [aboutOpen, setAboutOpen] = useState(false);
  const location = useLocation();
  // Anchor for the "Free explore →" header link so cold visitors can
  // jump straight to the mill picker section without scrolling past
  // the scenario cards.
  const millPickerRef = useRef<HTMLDivElement>(null);
  const scrollToMillPicker = () => {
    millPickerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // When the user clicks "← Back" on DetailView (or "Back to supply sheds"
  // off the last scenario step), the navigate call passes
  // `state: { fromDetail: true }` so we can distinguish a return-from-detail
  // mount from a fresh first visit. On return, the panel auto-scrolls to the
  // mill picker section so the user immediately sees their just-explored
  // selection (which useMillSelection has restored from localStorage)
  // instead of landing on the scrolled-to-top scenarios layout.
  // Mount-only by design — refs capture the initial values so the effect
  // doesn't re-fire if the user later interacts with the panel and
  // selectedMill or location.state changes.
  const cameFromDetailRef = useRef(
    (location.state as { fromDetail?: boolean } | null)?.fromDetail === true,
  );
  const initialMillRef = useRef(selectedMill);
  useEffect(() => {
    if (cameFromDetailRef.current && initialMillRef.current && millPickerRef.current) {
      // Instant scroll — `auto` lands the user on the picker without an
      // animated swoop the moment Explorer mounts. Smooth would be more
      // fluid but feels delayed for a reorientation cue.
      millPickerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, []);
  const stateOptions = STATES.map((state) => ({
    value: state,
    label: state,
  }));

  const millOptions = millsInState.map((mill) => ({
    value: mill.id,
    label: mill.display_name,
  }));

  const handleStateChange = (value: string) => {
    if (value) {
      onStateChange(value as StateType);
    } else {
      onStateChange(null);
    }
  };

  const handleMillChange = (value: string) => {
    if (value) {
      const mill = millsInState.find((m) => m.id === value);
      onMillChange(mill || null);
    } else {
      onMillChange(null);
    }
  };

  // Body content shared by the desktop side-rail and the mobile bottom
  // sheet. Identical markup so any layout/copy change lands in both places
  // automatically.
  const body = (
    <>
      <div className="px-5 sm:px-6 pt-5 sm:pt-6 flex flex-col gap-5">
        {/* Header row — co-branding lockup (CTrees + SFI) and demo badge.
            Hidden on mobile because the bottom-sheet peek bar already
            renders the lockup + DEMO badge as the persistent chrome. */}
        <div className="hidden md:flex items-center gap-2.5">
          <BrandLogo size={32} />
          <span aria-hidden="true" className="h-6 w-px bg-figma-ink-mute/40" />
          <SfiLogo size={32} />
          <DemoBadge />
        </div>

        {/* Page title block. Anton headline scales down on small screens
            so it doesn't blow out a 375px viewport. The framing paragraph
            below the H1 names what this is, what a supply shed is, and
            what the user can do — so a cold visitor doesn't have to infer
            the dashboard's purpose from the dropdowns alone. */}
        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-roboto font-bold uppercase tracking-[0.16em] text-figma-ink-mute">
            CTrees &times; SFI &middot; Proof of Concept
          </span>
          <h2 className="font-anton font-normal text-[32px] sm:text-[40px] leading-[1.05] tracking-[0.005em] uppercase text-figma-text m-0">
            Explore the data
          </h2>
          <p className="text-body font-roboto text-figma-ink-soft leading-relaxed">
            Forest data within drive-time <span className="text-figma-text font-semibold">supply sheds</span> &mdash; the
            area each mill sources its wood from &mdash; around 10 US mills in California, Oregon, Georgia,
            and Alabama.
          </p>
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setAboutOpen(true)}
              className="text-left text-[13px] font-roboto text-figma-mint hover:text-figma-lime focus:outline-none focus-visible:underline transition-smooth"
            >
              What is this? &rarr;
            </button>
            <button
              type="button"
              onClick={scrollToMillPicker}
              className="text-right text-[13px] font-roboto text-figma-mint hover:text-figma-lime focus:outline-none focus-visible:underline transition-smooth"
            >
              Browse mills &rarr;
            </button>
          </div>
        </div>
      </div>

      <div className="px-5 sm:px-6 py-6 flex flex-col gap-6 flex-1">
        <div className="h-px bg-figma-lime/20 w-full" />

        {/* Scenarios — primary entry point. Promoted above the mill picker
            because the curated walkthroughs are the recommended path for
            cold visitors at the SFI annual conference; the mill picker
            stays as the "free exploration" alternative below. */}
        <ScenarioSelector highlightOnMount={highlightScenarios} />

        {/* Mill picker — secondary path. Wrapped in its own elevated card
            so it visually balances the 3-card scenarios block above; with
            just two thin dropdowns the section read as an afterthought.
            The "Or find a mill" heading + "Free explore" pill mirror the
            scenario section's heading shape so the two paths read as
            paired alternatives. The ref is the scroll target for the
            "Free explore →" link in the header. */}
        <div
          ref={millPickerRef}
          className="pt-6 border-t border-figma-lime/20 flex flex-col gap-3 scroll-mt-4"
        >
          <div className="flex items-baseline justify-between">
            <h3 className="text-body font-roboto font-bold text-figma-text">
              Or find a mill
            </h3>
            <span className="text-xs font-roboto-mono text-figma-mint/80 uppercase tracking-wider">
              Free explore
            </span>
          </div>
          <p className="text-xs font-roboto text-figma-ink-soft leading-relaxed">
            Pick from 10 mills across California, Oregon, Georgia, and Alabama and explore the
            data yourself.
          </p>

          <div className="mt-1 flex flex-col gap-4 p-4 bg-figma-card rounded-figma-panel border border-figma-mint/30 shadow-figma-card">
            {/* State Selection */}
            <Dropdown
              label="State"
              placeholder="Choose a state..."
              value={selectedState || ''}
              options={stateOptions}
              onChange={handleStateChange}
            />

            {/* Supply Shed Selection */}
            <Dropdown
              label="Supply Shed"
              placeholder="Choose a supply shed..."
              value={selectedMill?.id || ''}
              options={millOptions}
              onChange={handleMillChange}
              disabled={!selectedState || millsInState.length === 0}
            />

            {/* Selection Info. The per-tree-data pill renders only on
                sheds with v1.2 per-crown coverage (Sierra Nevada 60-min)
                so users see the distinctive feature before drilling in
                — flags this mill as the per-tree showcase among the
                10 placeholder mills. */}
            {selectedMill && (
              <div className="pt-3 border-t border-figma-lime/20">
                <div className="flex flex-wrap items-baseline gap-2 mb-2">
                  <h4 className="text-body font-roboto font-bold text-figma-text">
                    {selectedMill.display_name}
                  </h4>
                  {isPerCrownAvailable(
                    (selectedMill.metadata?.code as string | undefined) ?? null,
                    60,
                  ) && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded bg-figma-yellow/15 border border-figma-yellow/50 text-figma-yellow text-[10px] font-roboto-mono font-bold uppercase tracking-wider leading-none"
                      title="The per-tree forest-attributes layer is published for this shed (Sierra Nevada, 60 min)."
                    >
                      Per-tree data
                    </span>
                  )}
                </div>
                <div className="space-y-1 text-body font-roboto">
                  <p className="text-figma-ink-soft">{selectedMill.state}</p>
                  <p className="text-xs font-roboto-mono text-figma-ink-mute">
                    {selectedMill.latitude.toFixed(2)}°N, {Math.abs(selectedMill.longitude).toFixed(2)}°W
                  </p>
                </div>
                {selectedMill.metadata?.description && (
                  <p className="mt-2 text-xs font-roboto text-figma-ink-soft">
                    {selectedMill.metadata.description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <FeedbackPanelLink />
      </div>
    </>
  );

  const isMobile = useIsMobile();

  // Render one variant or the other — never both. Two trees would mount
  // ScenarioSelector twice (the `data-scenario-selector` element used by
  // the ?mode=scenario nudge would resolve to the wrong one) and double
  // every dropdown's render cost.
  if (isMobile) {
    return (
      <>
        <MobileBottomSheet
          peekTitle={selectedMill?.display_name ?? 'Find a mill'}
          peekBadge={<DemoBadge />}
          initiallyExpanded
        >
          <ChromeBar />
          {body}
        </MobileBottomSheet>
        <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
      </>
    );
  }

  return (
    <>
      <div className="w-full md:w-[420px] lg:w-[460px] flex-1 md:flex-none md:flex-shrink-0 min-h-0 bg-figma-panel md:border-r border-white/10 flex flex-col md:h-full overflow-y-auto">
        <ChromeBar />
        {body}
      </div>
      <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </>
  );
};
